import { useState, type ReactNode } from 'react';
import type { ShoppingListSummary } from '../lib/domain';

// Issue #2: "Verschieben" steht gleichrangig neben Bearbeiten/Entfernen,
// nimmt aber standardmäßig kaum Platz ein - die Listenauswahl erscheint
// erst nach dem Klick, statt dauerhaft pro Position sichtbar zu sein.
// `triggerIcon` erlaubt es Aufrufern (z. B. dem Einkaufsmodus), statt des
// Textlabels ein kompaktes Symbol anzuzeigen; `triggerLabel` bleibt dabei
// als aria-label/title für Barrierefreiheit erhalten.
export function MoveToListControl({
  lists,
  onMove,
  disabled,
  triggerIcon,
  triggerLabel = 'Verschieben',
}: {
  lists: ShoppingListSummary[];
  onMove: (targetListId: string) => Promise<void> | void;
  disabled?: boolean;
  triggerIcon?: ReactNode;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  if (lists.length === 0) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label={triggerIcon ? triggerLabel : undefined}
        title={triggerIcon ? triggerLabel : undefined}
      >
        {triggerIcon ?? triggerLabel}
      </button>
    );
  }

  return (
    <div className="row" style={{ flexWrap: 'nowrap' }}>
      <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Zielliste" autoFocus>
        <option value="">Liste wählen…</option>
        {lists.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name || 'Ohne Namen'}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="primary"
        disabled={!target || busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onMove(target);
            setOpen(false);
            setTarget('');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? '…' : 'OK'}
      </button>
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setTarget('');
        }}
        disabled={busy}
        aria-label="Verschieben abbrechen"
      >
        ×
      </button>
    </div>
  );
}
